import React from "react";
import { useApolloClient } from "@apollo/client";

import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** providers */
import SAVE_SORTING from "../../provider/Calls/Sortings/mutation";

/** utils */
import { getGraphsqErros, maskRealBeautify } from "../../utilities/functions";

const Sorting: React.FC<iProps> = ({ person, setVisibleSorting }) => {
    const settings = useSettings();
    const apollo = useApolloClient();
    const toast = React.useRef<any>(null);

    const [loading, setLoading] = React.useState<boolean>(false);
    const [sorting, setSorting] = React.useState<iSorting | null>(person?.sorting ?? null);

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        let _record: any = { ...sorting };

        _record[name] = val;

        if (name === "weight" || name === "height") {
            const height = parseFloat(`${_record.height / 100 ?? 0}`);
            const weight = parseInt(`${_record.weight ?? 0}`);

            _record["imc"] = maskRealBeautify((weight / (height * height)).toFixed(2), false);
        }

        setSorting(_record);
    };

    const RenderFooter = () => (
        <>
            <div className="field col-12 md:col-12 ">
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    disabled={loading}
                    className="p-button-text text-red-500"
                    onClick={() => setVisibleSorting(false)}
                />
                <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
            </div>
        </>
    );

    const saveRecord = () => {
        const _record: any = { ...sorting };
        _record.mission_id = parseInt(settings.MISSION_ID);
        _record.patient_id = parseInt(`${person?.id}`);

        delete _record.__typename;

        setLoading(true);
        let mutation = SAVE_SORTING;

        if (sorting?.id) {
            _record.id = parseInt(`${_record.id}`);
        }

        apollo
            .mutate({
                mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

                setTimeout(() => setVisibleSorting(false), 1000);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    return (
        <>
            <Toast ref={toast} />
            <div className="flex flex-column md:flex-row flex-wrap">
                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="weight">
                        Peso <small>Em kg</small>
                    </label>
                    <InputText
                        type={"number"}
                        id="weight"
                        value={sorting?.weight}
                        onChange={e => onInputChange(e, "weight")}
                        className={classNames("p-3")}
                        autoFocus
                    />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="height">
                        Altura <small>Em centrimetros</small>
                    </label>
                    <InputText type={"number"} id="height" value={sorting?.height} onChange={e => onInputChange(e, "height")} className={classNames("p-3")} />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="imc">IMC</label>
                    <InputText id="imc" disabled value={sorting?.imc} onChange={e => onInputChange(e, "imc")} className={classNames("p-3")} />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="pressure">Pressão Arterial</label>
                    <InputText id="pressure" value={sorting?.pressure} onChange={e => onInputChange(e, "pressure")} className={classNames("p-3")} />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="temperature">Temperatura</label>
                    <InputText id="temperature" value={sorting?.temperature} onChange={e => onInputChange(e, "temperature")} className={classNames("p-3")} />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="respiratory_frequency">Frequencia Respiratória</label>
                    <InputText
                        id="respiratory_frequency"
                        value={sorting?.respiratory_frequency}
                        onChange={e => onInputChange(e, "respiratory_frequency")}
                        className={classNames("p-3")}
                    />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="heart_rate">Frequencia Cardíaca</label>
                    <InputText id="heart_rate" value={sorting?.heart_rate} onChange={e => onInputChange(e, "heart_rate")} className={classNames("p-3")} />
                </div>

                <div className="field col-12 md:col-6 flex flex-column">
                    <label htmlFor="saturation">Saturação</label>
                    <InputText
                        id="saturation"
                        style={{ width: "100%" }}
                        value={sorting?.saturation}
                        onChange={e => onInputChange(e, "saturation")}
                        className={classNames("p-3")}
                    />
                </div>

                <div className="field col-12 md:col-12 flex flex-column">
                    <label htmlFor="notes">Observações</label>
                    <InputTextarea
                        style={{ width: "100%", resize: "none" }}
                        id="notes"
                        value={sorting?.notes}
                        onChange={e => onInputChange(e, "notes")}
                        rows={3}
                    />
                </div>
            </div>
            <RenderFooter />
        </>
    );
};

export default Sorting;

interface iProps {
    person: iPeople | undefined;
    setVisibleSorting: Function;
}
